import type { ReactNode } from 'react';
import { Children, useEffect, useRef, useState } from 'react';

import { formatNumber, prefersReducedMotion } from './shared';

const easeOutCubic = (time: number) => 1 - (1 - time) ** 3;

/**
 * The design's count-up clock (`proofCount` / `countUp` in app.js): every time the block enters the viewport
 * (≥35% visible) the figures run 0 → value on a cubic ease-out, and leaving the viewport resets them so the
 * count replays on the next entrance. Reduced motion (and no IntersectionObserver) settles on the final values.
 * Returns the eased progress (0–1); the server render and the first paint carry the final values.
 */
export function useCountProgress<T extends Element>(duration: number) {
  const ref = useRef<T>(null);
  const [progress, setProgress] = useState(1);

  useEffect(() => {
    const element = ref.current;
    if (!element || !('IntersectionObserver' in window) || typeof window.requestAnimationFrame !== 'function') return undefined;
    const motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let raf = 0;
    let startedAt = 0;
    let played = false;

    const stop = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    };
    const tick = (now: number) => {
      if (!startedAt) startedAt = now;
      const time = Math.min(1, (now - startedAt) / duration);
      setProgress(easeOutCubic(time));
      raf = time < 1 ? window.requestAnimationFrame(tick) : 0;
    };
    const play = () => {
      stop();
      played = true;
      startedAt = 0;
      setProgress(0);
      raf = window.requestAnimationFrame(tick);
    };
    const reset = () => {
      stop();
      played = false;
      setProgress(0);
    };
    const finish = () => {
      stop();
      played = true;
      setProgress(1);
    };

    if (prefersReducedMotion()) finish();
    else setProgress(0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (motionQuery?.matches) {
            finish();
            return;
          }
          if (!entry.isIntersecting) reset();
          else if (entry.intersectionRatio >= 0.35 && !played) play();
        });
      },
      { threshold: [0, 0.35] }
    );
    observer.observe(element);
    const onMotionPref = () => {
      if (motionQuery?.matches) finish();
    };
    motionQuery?.addEventListener?.('change', onMotionPref);

    return () => {
      stop();
      observer.disconnect();
      motionQuery?.removeEventListener?.('change', onMotionPref);
    };
  }, [duration]);

  return [ref, progress] as const;
}

function textOf(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('');
}

interface CountFigureProps {
  /** The translated figure, e.g. `80%`, `%80`, `2000` or `300` (Trans passes the `<n>` tag's text here). */
  children?: ReactNode;
  progress: number;
  locale: string | undefined;
  /** Group digits with the locale's separator (2000 → 2.000 / 2,000). */
  grouped?: boolean;
  className?: string;
  valueClassName?: string;
}

/**
 * One counting figure: the final value for assistive tech (`sr-only`), an invisible copy that holds the box at
 * its final width (no reflow while counting) and the live value on top, as in the design's `.cu` / `.proof__n`.
 */
export function CountFigure({ children, progress, locale, grouped = false, className, valueClassName }: CountFigureProps) {
  const source = textOf(children);
  const digits = /\d+/.exec(source)?.[0] ?? '0';
  const target = parseInt(digits, 10) || 0;
  const render = (value: number) => source.replace(digits, grouped ? formatNumber(value, locale) : String(value));
  const finalText = render(target);

  return (
    <span className={className}>
      <span className='sr-only'>{finalText}</span>
      <span className='inline-grid' aria-hidden='true'>
        <span className='invisible [grid-area:1/1]'>{finalText}</span>
        <span className={`[grid-area:1/1] ${valueClassName ?? ''}`}>{render(Math.round(target * progress))}</span>
      </span>
    </span>
  );
}
